import DateRangeFilter from '@/components/DateRangeFilter';
import MetricChart from '@/components/MetricChart';
import { useToast } from '@/hooks/useToast';
import { projectsService } from '@/services/projectsService';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Project Dashboard page component
 * @returns {JSX.Element} ProjectDashboard component
 */
const ProjectDashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [project, setProject] = useState(null);

  // Date range state - default to last 7 days
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);
    startDate.setHours(0, 0, 0, 0);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  });

  // Fetch project details
  const {
    data: projectData,
    isLoading: projectLoading,
    error: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsService.getProject(projectId),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch project analytics with date filters
  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    error: analyticsError,
  } = useQuery({
    queryKey: ['project-analytics', projectId, dateRange],
    queryFn: () =>
      projectsService.getProjectAnalytics(projectId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      }),
    enabled: !!projectId && !!dateRange.startDate && !!dateRange.endDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Update project state when data is loaded
  // API returns { success: true, data: { ... } }
  useEffect(() => {
    if (projectData?.data) {
      setProject(projectData.data);
    }
  }, [projectData]);

  // Handle errors
  useEffect(() => {
    if (projectError) {
      showError('Failed to load project details.');
    }
    if (analyticsError) {
      showError('Failed to load analytics data.');
    }
  }, [projectError, analyticsError, showError]);

  // Loading state
  if (projectLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading project dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (projectError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 text-red-500">
            <svg fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Project not found
          </h3>
          <p className="mt-2 text-gray-500">
            The project you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/projects')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Back to Projects
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const analytics = analyticsData?.data || {};

  // Extract time series data from analytics response
  // Assuming the API returns data in format: { messages: [...], conversations: [...], etc. }
  const messagesData = analytics.messages || [];
  const conversationsData = analytics.conversations || [];
  const contactsData = analytics.contacts || [];
  const broadcastsData = analytics.broadcasts || [];
  const templatesData = analytics.templates || [];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* WhatsApp Connection Card - Show if not connected */}
        {(project.isWhatsappConnected === false ||
          project.isWhatsappConnected === undefined ||
          project.isWhatsappConnected === null) && (
          <div className="mb-8">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-yellow-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-lg font-medium text-yellow-800">
                    WhatsApp Not Connected
                  </h3>
                  <p className="mt-2 text-sm text-yellow-700">
                    Connect your WhatsApp Business account to enable messaging,
                    conversations, and broadcast features.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={() =>
                        navigate(
                          `/projects/${projectId}/whatsapp-configurations`
                        )
                      }
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-yellow-800 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                    >
                      <svg
                        className="h-5 w-5 mr-2"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                      </svg>
                      Connect WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Graphs Section */}
        <div className="mb-8">
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Analytics</h2>
              <DateRangeFilter
                onDateRangeChange={setDateRange}
                currentRange={dateRange}
              />
            </div>

            {analyticsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="ml-3 text-gray-600">Loading analytics...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricChart
                  title="Total Messages"
                  data={messagesData}
                  dataKey="date"
                  valueKey="count"
                  color="#3b82f6"
                />
                <MetricChart
                  title="Total Conversations"
                  data={conversationsData}
                  dataKey="date"
                  valueKey="count"
                  color="#10b981"
                />
                <MetricChart
                  title="Total Contacts"
                  data={contactsData}
                  dataKey="date"
                  valueKey="count"
                  color="#8b5cf6"
                />
                <MetricChart
                  title="Total Broadcasts"
                  data={broadcastsData}
                  dataKey="date"
                  valueKey="count"
                  color="#f59e0b"
                />
                <MetricChart
                  title="Total Templates"
                  data={templatesData}
                  dataKey="date"
                  valueKey="count"
                  color="#ef4444"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;
